import { alpha } from '@mui/material/styles'

// Translucent so what passes underneath is still felt, blurred so what sits on top
// stays readable. Paper rather than grey: incoming message bubbles are `grey.100`, and
// something floating must not read as one of them.
const glass = (theme) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.76),
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
})

// The edges of a pane of glass, carried in one shadow rather than a border: a hairline
// ring, a contact shadow beneath it, and a sheen along the top edge. The sheen is what
// reads as glass rather than a tinted box.
const RING = '0 0 0 0.5px rgba(0, 0, 0, 0.08)'
const CONTACT = '0 1px 2px rgba(0, 0, 0, 0.04)'
const SHEEN = 'inset 0 1px 0 rgba(255, 255, 255, 0.9)'
const edges = (lift) => [RING, CONTACT, lift, SHEEN].join(', ')

/** Navigation fixed to an edge of the screen: the side panel and the tab bar. */
export const glassChrome = (theme) => ({
  ...glass(theme),
  boxShadow: edges('0 16px 40px -12px rgba(0, 0, 0, 0.18)'),
})

/**
 * A control floating just above the page it belongs to: the conversation header, the
 * composer, the conversation search. Closer to the page than navigation, so it casts a
 * tighter shadow.
 */
export const frostedPill = (theme) => ({
  ...glass(theme),
  borderRadius: theme.spacing(3),
  boxShadow: edges('0 6px 16px -8px rgba(0, 0, 0, 0.24)'),
})

/**
 * A card resting on the page rather than floating above it: the same ring and contact
 * shadow the glass carries, but no sheen — there is no light to catch on something
 * opaque — and only enough lift to lay it down. Replaces a border and an elevation used
 * together, which gave every card two edges for one job.
 */
export const cardSurface = {
  position: 'relative',
  borderRadius: 2,
  boxShadow: [RING, CONTACT, '0 4px 12px -6px rgba(0, 0, 0, 0.12)'].join(', '),
}

/**
 * A surface that reads as pressable without drawing a rectangle around itself. Rows,
 * cards and tiles all sit on this, so a screen made of them reads as one family rather
 * than a stack of boxes.
 */
export const tintedSurface = {
  borderRadius: 2,
  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
}

/** The same surface where the whole of it is the target, so it answers the pointer. */
export const tintedAction = {
  ...tintedSurface,
  transition: 'background-color 150ms ease-out',
  '&:hover': { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08) },
}

/**
 * The card the record screens are built from: vitals, health history, insurance.
 *
 * One titled card holding a list of entries, where the header is the whole control.
 * The padding sits on the header and the list rather than on the card, so the header
 * can fill the width it answers the pointer across.
 */
export const recordCard = {
  card: { ...cardSurface, display: 'flex', flexDirection: 'column', overflow: 'hidden' },

  /** For a card whose header is not a control, so the padding stays on the card itself */
  padded: { px: 2, py: 1.5 },

  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 },
  pressable: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    flex: 1,
    minWidth: 0,
    pl: 2,
    pr: 1,
    py: 1.5,
    textAlign: 'left',
    transition: 'background-color 150ms ease-out',
    '&:hover': { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04) },
  },

  /** The icon closing the pressable region: a chevron, or the action it opens */
  trailing: { color: 'text.secondary' },

  /** An action that sits beside the pressable region rather than inside it */
  add: { flexShrink: 0, mr: 2 },

  heading: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  titleRow: { display: 'flex', alignItems: 'baseline', gap: 1 },

  /** The supporting line under the title, such as "Latest:" or "Last updated:" */
  meta: { display: 'flex', gap: 0.5 },

  // Its own top gap: the header's padding belongs to the surface you press, not the list
  items: { display: 'flex', flexDirection: 'column', gap: 1, px: 2, pt: 1, pb: 1.5 },
  item: { ...tintedSurface, px: 1.5, py: 1 },
}

/**
 * The rounded square a row or a notification leads with, holding an icon.
 *
 * @param {number} [size=36] - Width and height in pixels.
 * @returns {object} The style object.
 */
export const iconTile = (size = 36) => ({
  width: size,
  height: size,
  borderRadius: 1.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
})

/**
 * The way back, drawn as a control rather than a hint: an arrow on a filled circle. A
 * bare chevron sat on the bar like punctuation and was easy to miss.
 */
export const backButton = {
  ...tintedAction,
  borderRadius: '50%',
  color: 'text.primary',
}

/** A destructive action in the page bar: the same circle, washed with what it does. */
export const dangerButton = {
  borderRadius: '50%',
  color: 'error.main',
  backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
  transition: 'background-color 150ms ease-out',
  '&:hover': { backgroundColor: (theme) => alpha(theme.palette.error.main, 0.16) },
}

/**
 * The shape a navigation destination takes: a capsule that fills with the accent when
 * it is the one you are on, and gives a little when pressed.
 *
 * @param {boolean} active - Whether the destination owns the current route.
 * @returns {object} The style object.
 */
export const navCapsule = (active) => ({
  borderRadius: 100,
  textDecoration: 'none',
  color: active ? 'secondary.main' : 'text.secondary',
  transition: 'background-color 200ms ease-out, color 200ms ease-out, transform 200ms cubic-bezier(0.2, 0, 0, 1)',
  '&:hover': {
    backgroundColor: (theme) => alpha(theme.palette.secondary.main, active ? 0.18 : 0.06),
  },
  '&:active': { transform: 'scale(0.97)' },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:active': { transform: 'none' } },
})

export const outlineRichTextEditorStyling = {
  p: 1,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  '&:focus-within': {
    outline: '1px solid',
    outlineColor: 'primary.main',
    borderColor: 'primary.main',
  },
}
